import { request } from '@reflex/ui/client/http';
import type { FlowActionCatalogEntry } from '../shared/action-catalog';
import type { FlowEventCatalogEntry } from '../shared/event-catalog';
import type { FieldOptionsResult } from '../shared/field-options';
import type { ExternalRefLookupResult } from '../shared/external-refs';
import type { FlowTemplateAvailability } from '../shared/templates';
import type {
  AxonEvent,
  ChangeTitle,
  ChangeTitleRequest,
  VersionSummary,
  VersionDetail,
  DiffSummary,
} from '@reflex/shared';
import type {
  FlowDefinition,
  FlowRun,
  FlowRunInbox,
  FlowRunSummaryPage,
  FlowRunStats,
  FlowRunNode,
  FlowApprovalSubmission,
  CreateFlowDefinition,
  UpdateFlowDefinition,
} from '@reflex/flows';

export interface ActiveFlowRunSnapshot {
  run: FlowRun;
  flow: FlowDefinition;
  nodes: FlowRunNode[];
}

export interface GenerateFlowRequest {
  prompt: string;
  model?: string;
}

export interface FlowCapabilities {
  models: Array<{ name: string; plugin: string; description: string; fields: string[] }>;
  triggers: Array<{
    type: string;
    plugin: string;
    description: string;
    payload: string;
    source: string;
    actions?: string[];
  }>;
  actions: Array<{ key: string; plugin: string; description: string; fields: string[] }>;
}

export const flowsApi = {
  getCapabilities: () => request<FlowCapabilities>('/flows/capabilities'),
  // Per-org availability of the gallery templates. Hidden templates (the org
  // can't satisfy a required plugin's gate) are omitted by the server.
  getTemplates: () => request<FlowTemplateAvailability[]>('/flows/templates'),
  getActionCatalog: () => request<FlowActionCatalogEntry[]>('/flow-action-catalog'),
  getEventCatalog: () => request<FlowEventCatalogEntry[]>('/flows/event-catalog'),
  // Option list for a semantic payload-field type (e.g. 'linear:user'),
  // served by the field-option provider registry.
  getFieldOptions: (fieldType: string) =>
    request<FieldOptionsResult>(`/flows/field-options?fieldType=${encodeURIComponent(fieldType)}`),
  // Display profiles for provider entity ids found in a trigger payload or
  // an action's params, served by the external-ref resolver registry.
  getExternalRefs: (fieldType: string, ids: string[]) =>
    request<ExternalRefLookupResult>(
      `/flows/external-refs?fieldType=${encodeURIComponent(fieldType)}&ids=${encodeURIComponent(ids.join(','))}`,
    ),
  generateFlow: (data: GenerateFlowRequest) =>
    request<CreateFlowDefinition>('/flows/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  listFlows: () => request<FlowDefinition[]>('/flows'),
  getFlow: (id: string) => request<FlowDefinition>(`/flows/${encodeURIComponent(id)}`),
  createFlow: (data: CreateFlowDefinition) =>
    request<FlowDefinition>('/flows', { method: 'POST', body: JSON.stringify(data) }),
  updateFlow: (
    id: string,
    data: UpdateFlowDefinition,
    options?: { skipConnectivityChecks?: boolean },
  ) => {
    const path = options?.skipConnectivityChecks
      ? `/flows/${encodeURIComponent(id)}?skipConnectivityChecks=true`
      : `/flows/${encodeURIComponent(id)}`;
    return request<FlowDefinition>(path, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  deleteFlow: (id: string) =>
    request<{ success: boolean }>(`/flows/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  toggleFlow: (id: string, enabled: boolean) =>
    request<FlowDefinition>(`/flows/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),
  triggerFlow: (id: string, triggerData?: Record<string, unknown>, triggerNodeId?: string) =>
    request<FlowRun>(`/flows/${encodeURIComponent(id)}/trigger`, {
      method: 'POST',
      body: JSON.stringify({ triggerData, triggerNodeId }),
    }),

  // The list endpoint returns one keyset-paginated page of slim
  // `FlowRunSummary` rows (no `state`/`triggerData`); pass `cursor` (from the
  // previous page's `nextCursor`) to fetch the next page. Full-run reads go
  // through `getFlowRun`.
  listFlowRuns: (flowId?: string, opts?: { cursor?: string; signal?: AbortSignal }) => {
    const params = new URLSearchParams();
    if (flowId) params.set('flowId', flowId);
    if (opts?.cursor) params.set('cursor', opts.cursor);
    const qs = params.toString();
    return request<FlowRunSummaryPage>(
      qs ? `/flow-runs?${qs}` : '/flow-runs',
      opts?.signal ? { signal: opts.signal } : undefined,
    );
  },
  // Per-flow aggregate rollup (totals, per-status counts, latest run) for the
  // runs dashboards. Cheap to compute server-side and bounded by flow count.
  getFlowRunStats: (opts?: { signal?: AbortSignal }) =>
    request<FlowRunStats[]>('/flow-runs/stats', opts?.signal ? { signal: opts.signal } : undefined),
  listActiveFlowRuns: () => request<ActiveFlowRunSnapshot[]>('/flow-runs/active'),
  // Owner-scoped inbox slice — `{ errors, recentCompleted }`. Independent of
  // `/flow-runs` so the inbox never rides the org-wide runs-list firehose.
  getInboxRuns: (opts?: { signal?: AbortSignal }) =>
    request<FlowRunInbox>('/flow-runs/inbox', opts?.signal ? { signal: opts.signal } : undefined),
  getFlowRun: (id: string) => request<FlowRun>(`/flow-runs/${encodeURIComponent(id)}`),
  getFlowRunNodes: (runId: string) =>
    request<FlowRunNode[]>(`/flow-runs/${encodeURIComponent(runId)}/nodes`),
  getFlowRunStream: (runId: string) =>
    request<{ events: AxonEvent[]; streamIds: string[] }>(
      `/flow-runs/${encodeURIComponent(runId)}/stream`,
    ),
  submitApproval: (runId: string, nodeId: string, response: FlowApprovalSubmission) =>
    request<{ success: boolean }>(
      `/flow-runs/${encodeURIComponent(runId)}/nodes/${encodeURIComponent(nodeId)}/approve`,
      { method: 'POST', body: JSON.stringify(response) },
    ),
  sendFlowNodeMessage: (runId: string, nodeId: string, message: string) =>
    request<{ success: boolean }>(
      `/flow-runs/${encodeURIComponent(runId)}/nodes/${encodeURIComponent(nodeId)}/message`,
      { method: 'POST', body: JSON.stringify({ message }) },
    ),
  cancelFlowRun: (runId: string) =>
    request<{ success: boolean }>(`/flow-runs/${encodeURIComponent(runId)}/cancel`, {
      method: 'POST',
    }),
  // --- Versioning ---

  listVersions: (id: string) =>
    request<VersionSummary[]>(`/flows/${encodeURIComponent(id)}/versions`),
  saveVersion: (id: string, body: { note?: string } = {}) =>
    request<VersionSummary>(`/flows/${encodeURIComponent(id)}/versions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getVersion: (id: string, versionId: string) =>
    request<VersionDetail>(
      `/flows/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`,
    ),
  /** Rename a version. `null` clears the note. */
  updateVersionNote: (id: string, versionId: string, note: string | null) =>
    request<VersionSummary>(
      `/flows/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`,
      { method: 'PATCH', body: JSON.stringify({ note }) },
    ),
  /** Suggested title for the pending change, used as the version note. */
  getChangeTitle: (id: string, body: ChangeTitleRequest = {}) =>
    request<ChangeTitle>(`/flows/${encodeURIComponent(id)}/change-title`, {
      method: 'POST',
      body: JSON.stringify(body.previousTitle ? { previousTitle: body.previousTitle } : {}),
    }),
  publish: (id: string, body: { note?: string; source?: string } = {}) =>
    request<{ version: VersionSummary }>(`/flows/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  publishVersion: (id: string, versionId: string) =>
    request<{ success: boolean }>(
      `/flows/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/publish`,
      { method: 'POST' },
    ),
  unpublish: (id: string) =>
    request<{ success: boolean }>(`/flows/${encodeURIComponent(id)}/unpublish`, {
      method: 'POST',
    }),
  restoreVersion: (id: string, versionId: string, body: { force?: boolean } = {}) =>
    request<{ success: boolean }>(
      `/flows/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/restore`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  discardDraft: (id: string) =>
    request<{ success: boolean }>(`/flows/${encodeURIComponent(id)}/discard-draft`, {
      method: 'POST',
    }),
  /**
   * Draft-vs-live diff. `withChanges` opts into the itemized change list;
   * without it the response carries counts only, which is all the editor's
   * always-mounted diff query needs.
   */
  getDiff: (id: string, opts: { withChanges?: boolean } = {}) =>
    request<DiffSummary>(
      `/flows/${encodeURIComponent(id)}/diff${opts.withChanges ? '?changes=1' : ''}`,
    ),
};
